import type { ComponentProps } from 'react';

import { Autocomplete as AutocompletePrimitive } from '@base-ui/react/autocomplete';
import { VariantProps, cva } from 'class-variance-authority';
import { ChevronsUpDownIcon, XIcon } from 'lucide-react';

import { Input } from 'components/input/Input';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

import { ScrollArea } from './scroll-area';

const inputVariants = cva('', {
  variants: {
    size: {
      sm: 'h-7 px-2',
      default: 'h-8 px-3',
      lg: 'h-9 px-3',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const Autocomplete = AutocompletePrimitive.Root;

function AutocompleteValue(props: AutocompletePrimitive.Value.Props) {
  return (
    <AutocompletePrimitive.Value data-slot='autocomplete-value' {...props} />
  );
}

function AutocompleteInput({
  className,
  size = 'default',
  showClear = false,
  showTrigger = false,
  ...props
}: Omit<AutocompletePrimitive.Input.Props, 'size'> &
  VariantProps<typeof inputVariants> & {
    showClear?: boolean;
    showTrigger?: boolean;
  }) {
  return (
    <div className='relative w-full'>
      <AutocompletePrimitive.Input
        render={<Input />}
        data-slot='autocomplete-input'
        data-size={size}
        className={cn(
          inputVariants({ size }),
          (showClear || showTrigger) && 'pr-8',
          className
        )}
        {...props}
      />
      {showTrigger && <AutocompleteTrigger />}
      {showClear && <AutocompleteClear />}
    </div>
  );
}

function AutocompleteStatus({
  className,
  ...props
}: AutocompletePrimitive.Status.Props) {
  return (
    <AutocompletePrimitive.Status
      data-slot='autocomplete-status'
      className={cn(
        'px-3 py-2 text-[13px] text-neutral-600 empty:m-0 empty:p-0',
        className
      )}
      {...props}
    />
  );
}

function AutocompletePortal({
  container,
  ...props
}: AutocompletePrimitive.Portal.Props) {
  // Render inside the Drumkit root: Vulcan scopes Tailwind and the theme to it,
  // so a popup on document.body is unstyled. Base UI never mounts on an
  // explicit null container, so fall back to its default (document.body).
  const drumkitContainer = getDrumkitContainer();

  return (
    <AutocompletePrimitive.Portal
      data-slot='autocomplete-portal'
      container={
        container ??
        (drumkitContainer instanceof HTMLElement ? drumkitContainer : undefined)
      }
      {...props}
    />
  );
}

function AutocompleteBackdrop(props: AutocompletePrimitive.Backdrop.Props) {
  return (
    <AutocompletePrimitive.Backdrop
      data-slot='autocomplete-backdrop'
      {...props}
    />
  );
}

function AutocompletePositioner({
  className,
  ...props
}: AutocompletePrimitive.Positioner.Props) {
  return (
    <AutocompletePrimitive.Positioner
      data-slot='autocomplete-positioner'
      className={cn('z-50 outline-none', className)}
      {...props}
    />
  );
}

function AutocompleteList({
  className,
  scrollAreaClassName,
  ...props
}: AutocompletePrimitive.List.Props & {
  scrollAreaClassName?: string;
}) {
  return (
    <ScrollArea className={cn('size-full min-h-0', scrollAreaClassName)}>
      <AutocompletePrimitive.List
        data-slot='autocomplete-list'
        className={cn('not-empty:p-1 not-empty:scroll-py-1', className)}
        {...props}
      />
    </ScrollArea>
  );
}

function AutocompleteCollection(
  props: ComponentProps<typeof AutocompletePrimitive.Collection>
) {
  return (
    <AutocompletePrimitive.Collection
      data-slot='autocomplete-collection'
      {...props}
    />
  );
}

function AutocompleteRow({
  className,
  ...props
}: ComponentProps<typeof AutocompletePrimitive.Row>) {
  return (
    <AutocompletePrimitive.Row
      data-slot='autocomplete-row'
      className={cn('flex items-center gap-2', className)}
      {...props}
    />
  );
}

function AutocompleteItem({
  className,
  ...props
}: ComponentProps<typeof AutocompletePrimitive.Item>) {
  return (
    <AutocompletePrimitive.Item
      data-slot='autocomplete-item'
      className={cn(
        'relative flex cursor-default items-center gap-1.5 rounded px-2 py-2 text-xs text-neutral-800 outline-none select-none',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        'data-highlighted:bg-neutral-100',
        className
      )}
      {...props}
    />
  );
}

export interface AutocompleteContentProps extends ComponentProps<
  typeof AutocompletePrimitive.Popup
> {
  align?: AutocompletePrimitive.Positioner.Props['align'];
  sideOffset?: AutocompletePrimitive.Positioner.Props['sideOffset'];
  alignOffset?: AutocompletePrimitive.Positioner.Props['alignOffset'];
  side?: AutocompletePrimitive.Positioner.Props['side'];
  anchor?: AutocompletePrimitive.Positioner.Props['anchor'];
  showBackdrop?: boolean;
}

function AutocompleteContent({
  className,
  children,
  showBackdrop = false,
  align = 'start',
  sideOffset = 4,
  alignOffset = 0,
  side = 'bottom',
  anchor,
  ...props
}: AutocompleteContentProps) {
  return (
    <AutocompletePortal>
      {showBackdrop && <AutocompleteBackdrop />}
      <AutocompletePositioner
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        side={side}
        anchor={anchor}
      >
        <div className='relative flex max-h-full'>
          <AutocompletePrimitive.Popup
            data-slot='autocomplete-popup'
            className={cn(
              'flex max-h-[min(var(--available-height),24rem)] w-[var(--anchor-width)] max-w-[var(--available-width)] origin-[var(--transform-origin)] flex-col overflow-hidden rounded border border-neutral-300 bg-neutral-50 py-0.5 text-neutral-800 shadow-lg',
              'transition-[scale,opacity] data-starting-style:scale-98 data-starting-style:opacity-0',
              className
            )}
            {...props}
          >
            {children}
          </AutocompletePrimitive.Popup>
        </div>
      </AutocompletePositioner>
    </AutocompletePortal>
  );
}

function AutocompleteGroup(
  props: ComponentProps<typeof AutocompletePrimitive.Group>
) {
  return (
    <AutocompletePrimitive.Group data-slot='autocomplete-group' {...props} />
  );
}

function AutocompleteGroupLabel({
  className,
  ...props
}: ComponentProps<typeof AutocompletePrimitive.GroupLabel>) {
  return (
    <AutocompletePrimitive.GroupLabel
      data-slot='autocomplete-group-label'
      className={cn(
        'px-2 py-1 text-xs font-medium text-neutral-600',
        className
      )}
      {...props}
    />
  );
}

function AutocompleteEmpty({
  className,
  ...props
}: ComponentProps<typeof AutocompletePrimitive.Empty>) {
  return (
    <AutocompletePrimitive.Empty
      data-slot='autocomplete-empty'
      className={cn(
        'px-3 py-2 text-center text-[13px] text-neutral-600 empty:m-0 empty:p-0',
        className
      )}
      {...props}
    />
  );
}

function AutocompleteClear({
  className,
  ...props
}: ComponentProps<typeof AutocompletePrimitive.Clear>) {
  return (
    <AutocompletePrimitive.Clear
      data-slot='autocomplete-clear'
      className={cn(
        'absolute end-2 top-1/2 -translate-y-1/2 cursor-pointer opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none',
        className
      )}
      {...props}
    >
      <XIcon className='size-4' />
    </AutocompletePrimitive.Clear>
  );
}

function AutocompleteTrigger({
  className,
  ...props
}: ComponentProps<typeof AutocompletePrimitive.Trigger>) {
  return (
    <AutocompletePrimitive.Trigger
      data-slot='autocomplete-trigger'
      className={cn(
        'absolute end-2 top-1/2 -translate-y-1/2 cursor-pointer opacity-70 focus:outline-none disabled:pointer-events-none',
        'has-[+[data-slot=autocomplete-clear]]:hidden',
        className
      )}
      {...props}
    >
      <ChevronsUpDownIcon className='size-4' />
    </AutocompletePrimitive.Trigger>
  );
}

function AutocompleteArrow(props: AutocompletePrimitive.Arrow.Props) {
  return (
    <AutocompletePrimitive.Arrow data-slot='autocomplete-arrow' {...props} />
  );
}

function AutocompleteSeparator({
  className,
  ...props
}: ComponentProps<typeof AutocompletePrimitive.Separator>) {
  return (
    <AutocompletePrimitive.Separator
      data-slot='autocomplete-separator'
      className={cn('my-1.5 h-px bg-neutral-200', className)}
      {...props}
    />
  );
}

export {
  Autocomplete,
  AutocompleteArrow,
  AutocompleteBackdrop,
  AutocompleteClear,
  AutocompleteCollection,
  AutocompleteContent,
  AutocompleteEmpty,
  AutocompleteGroup,
  AutocompleteGroupLabel,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
  AutocompletePortal,
  AutocompletePositioner,
  AutocompleteRow,
  AutocompleteSeparator,
  AutocompleteStatus,
  AutocompleteTrigger,
  AutocompleteValue,
};
